import { GetDesignsByDesignValidateApiResponse, ProductVariantResource, useGetProductVariantsQuery } from '@/api/serverApi'
import { RootState } from '@/stores'
import { Button, LoadingSpinner, SearchIcon, TextInput } from '@cfs-fixings/shared'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'
import { SmallCheckbox } from '@cfs-fixings/shared/src/components/checkbox'
import ComponentType from '@/enums/ComponentType'
import ProductOption from './components/ProductOption'
import { clearSelectedProduct } from '@/stores/product-picker'
import TBoltOptionType from './components/product-configuration-table/types/TBoltOptionType'

type ProductPickerPropTypes = {
  isOpen: boolean
  onClose: () => void
  productType?: string
  parentId?: string
  onConfirm?: (product: ProductVariantResource, configurationOption?: TBoltOptionType) => void
  onValidate?: (product: ProductVariantResource) => Promise<GetDesignsByDesignValidateApiResponse | undefined>
}

type ProductSectionPropTypes = {
  header: string
  selectedLabel: string
  products?: ProductVariantResource[]
  onValidate?: (product: ProductVariantResource) => Promise<GetDesignsByDesignValidateApiResponse | undefined>
  isConfigurable?: boolean
}

const ProductSection = ({ header, selectedLabel, products = [], onValidate, isConfigurable }: ProductSectionPropTypes) => {

  return (
    <>
      <h1 className="text-xs font-semibold leading-[14.4px] text-gray-600">{header}</h1>
      {
        products?.map((product) => (
          <ProductOption
            key={product.id}
            product={product}
            selectedLabel={selectedLabel}
            onValidate={onValidate}
            isConfigurable={isConfigurable}
          />
        ))
      }
    </>
  )
}

const ProductPicker = ({ isOpen, onClose, productType, parentId, onConfirm, onValidate }: ProductPickerPropTypes) => {
  const dispatch = useDispatch()

  const design = useSelector((state: RootState) => state.activeDesign.design)
  const selectedProduct = useSelector((state: RootState) => state.productPickerSlice.selectedProduct)
  const selectedConfigurationOption = useSelector((state: RootState) => state.productPickerSlice.selectedProductConfigurationOption)

  const component = design?.components.find((component) => component.id === parentId)

  const [search, setSearch] = useState<string | undefined | null>(null)

  const [selectedMaterialFilters, setSelectedMaterialFilters] = useState<string[]>([])
  const [selectedVendorFilters, setSelectedVendorFilters] = useState<string[]>([])

  const setDebouncedSearch = useDebouncedCallback(setSearch, 1000)

  const { data, isFetching } = useGetProductVariantsQuery({
    productType,
    ...(component?.product_variant_id && { parentProductVariantId: component?.product_variant_id }),
    ...(search && search !== '' && { search }),
  })

  const castInAnchorChannels = data?.data
    ?.filter((product) => product.product_type === ComponentType.CastInChannel)
    .filter((product) => selectedMaterialFilters.length === 0 || selectedMaterialFilters.includes(product.product_material))
    .filter((product) => selectedVendorFilters.length === 0 || selectedVendorFilters.includes(product.vendor_name))

  const tBolts = data?.data
    ?.filter((product) => product.product_type === ComponentType.TBolt)
    .filter((product) => selectedMaterialFilters.length === 0 || selectedMaterialFilters.includes(product.product_material))
    .filter((product) => selectedVendorFilters.length === 0 || selectedVendorFilters.includes(product.vendor_name))

  const materialFilters = useMemo(() => {
    if (!data?.data) {
      return null
    }

    return data?.data?.reduce((acc: string[], product) => {
      if (!acc.includes(product.product_material)) {
        acc.push(product.product_material)
      }

      return acc
    }, [])
  }, [data?.data])

  const vendorFilters = useMemo(() => {
    if (!data?.data) {
      return null
    }

    return data?.data?.reduce((acc: string[], product) => {
      if (!acc.includes(product.vendor_name)) {
        acc.push(product.vendor_name)
      }

      return acc
    }, [])
  }, [data?.data])

  useEffect(() => {
    if (selectedMaterialFilters.some((material) => !materialFilters?.includes(material))) {
      setSelectedMaterialFilters([
        ...selectedMaterialFilters.filter((material) => materialFilters?.includes(material)),
      ])
    }

    if (selectedVendorFilters.some((vendor) => !vendorFilters?.includes(vendor))) {
      setSelectedVendorFilters([
        ...selectedVendorFilters.filter((vendor) => vendorFilters?.includes(vendor)),
      ])
    }
  }, [materialFilters, selectedMaterialFilters, selectedVendorFilters, vendorFilters])

  return (
    <>
      <Transition
        appear
        show={isOpen}
        as={Fragment}
      >
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setSearch(null)
            setSelectedMaterialFilters([])
            setSelectedVendorFilters([])

            dispatch(clearSelectedProduct())

            onClose()
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-7xl transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all">
                  <div className="flex flex-col w-full h-[75vh]">
                    <div className="border-b border-gray-60">
                      <div className="flex flex-row justify-between items-center py-2.5 pr-5 pl-2">
                        <div className="w-full pr-4">
                          <TextInput
                            variant="borderless"
                            placeholder="Search Products"
                            prefixIcon={<SearchIcon className="text-gray-600 h-4 w-4" />}
                            onChange={(e) => setDebouncedSearch(e.target.value)}
                          />
                        </div>
                        <div className="flex flex-row gap-2 shrink-0">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setSearch(null)
                              setSelectedMaterialFilters([])
                              setSelectedVendorFilters([])

                              dispatch(clearSelectedProduct())

                              onClose()
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="dark"
                            size="sm"
                            onClick={() => {
                              if (!selectedProduct) {
                                return
                              }

                              setSearch(null)
                              setSelectedMaterialFilters([])
                              setSelectedVendorFilters([])

                              dispatch(clearSelectedProduct())

                              onConfirm?.(selectedProduct!, selectedConfigurationOption)
                            }}
                          >
                            Confirm Selection
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-row w-full min-h-0 h-full">
                      <div className="border-r border-gray-60 min-w-[202px] px-5 flex flex-col justify-start items-start py-5">
                        <p className="text-[10px] leading-3 font-semibold tracking-[0.02em] text-gray-600 pb-3">MANUFACTURERS</p>
                        <div className="flex flex-col gap-3">
                          {
                            vendorFilters?.map((vendor) => (
                              <SmallCheckbox
                                key={vendor}
                                name={vendor}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedVendorFilters([...selectedVendorFilters, vendor])
                                  } else {
                                    setSelectedVendorFilters(selectedVendorFilters.filter((item) => item !== vendor))
                                  }
                                }}
                              >
                                {vendor}
                              </SmallCheckbox>
                            ))
                          }
                        </div>
                        <p className="text-[10px] leading-3 font-semibold tracking-[0.02em] text-gray-600 pt-8 pb-3">MATERIALS</p>
                        <div className="flex flex-col gap-3">
                          {
                            materialFilters?.map((material) => (
                              <SmallCheckbox
                                key={material}
                                name={material}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedMaterialFilters([...selectedMaterialFilters, material])
                                  } else {
                                    setSelectedMaterialFilters(selectedMaterialFilters.filter((item) => item !== material))
                                  }
                                }}
                              >
                                {material}
                              </SmallCheckbox>
                            ))
                          }
                        </div>
                      </div>
                      <div className="flex flex-col gap-3 px-5 py-5 w-full min-h-0 h-full overflow-scroll">
                        {
                          isFetching
                            && (
                              <div className="flex h-full w-full justify-center items-center">
                                <LoadingSpinner className="h-4 w-4" />
                              </div>
                            )
                        }
                        {
                          !isFetching && (castInAnchorChannels?.length ?? 0) > 0
                            && (
                              <ProductSection
                                header="CAST-IN ANCHOR CHANNELS"
                                selectedLabel="Selected Channel"
                                products={castInAnchorChannels}
                                onValidate={onValidate}
                                isConfigurable
                              />
                            )
                        }
                        {
                          !isFetching && (tBolts?.length ?? 0) > 0
                            && (
                              <ProductSection
                                header="T-BOLTS"
                                selectedLabel="Selected T-Bolt"
                                products={tBolts}
                                onValidate={onValidate}
                              />
                            )
                        }
                        {
                          data?.data?.length === 0
                            && (
                              <div className="flex h-full w-full justify-center items-center">
                                <p className="text-sm leading-[18px]">No products available</p>
                              </div>
                            )
                        }
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default ProductPicker
